import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { AssistSession } from '../api/types';

export interface ActiveCall {
  session: AssistSession;
  roomUrl: string;
  token: string;
}

interface AssistCall {
  call: ActiveCall | null;
  start: (call: ActiveCall) => void;
  /** End the session for everyone: the room is deleted. */
  finish: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

/**
 * The screen-share call, held by the console rather than by the Assist screen.
 *
 * It used to be the Assist screen's local state, so ANY sidebar click -- and
 * the sidebar stays on screen during a call -- unmounted the stage, which left
 * the Daily room without ever calling /end. Back on Assist the session showed
 * a disabled "In progress" button, with no way to rejoin or to end it, and the
 * operator sat looking at a support person who had silently gone until the
 * expiry sweep. Now the call lives here, above the routes: leaving Assist hides
 * the stage (components/AssistCallHost.tsx) instead of unmounting it, so the call stays joined, a bar on every
 * other screen says so with Return and End, and reloading the page asks first.
 *
 * Null outside a provider -- the fixture preview renders screens without one,
 * and the Assist screen then holds its call itself, as it always did.
 */
export const AssistCallContext = createContext<AssistCall | null>(null);

export const useAssistCall = () => useContext(AssistCallContext);

export function AssistCallProvider({ children }: { children: ReactNode }) {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A reload or a closed tab still ends the call -- nothing can keep a Daily
  // connection across a page load -- so the browser asks first.
  useEffect(() => {
    if (!call) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [call]);

  const finish = useCallback(async () => {
    if (!call) return;
    const id = call.session.id;
    // The stage is torn down FIRST. Ending the session deletes the room, and a
    // call object still joined to a deleted room surfaces as an error dialog on
    // top of a console that is otherwise behaving correctly.
    setCall(null);
    try {
      await api.post(`/assist/${id}/end`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [call]);

  const value = useMemo(
    () => ({ call, start: setCall, finish, error, clearError: () => setError(null) }),
    [call, finish, error],
  );
  return <AssistCallContext.Provider value={value}>{children}</AssistCallContext.Provider>;
}
